import React, { createContext, useState, useCallback ,useEffect} from "react";
import { getEmployees, addEmployee as addEmployeeService ,updateEmployee as updateEmployeeService,getEmployeeById as getEmployeeByIdApi ,getbgCheck as getbgCheckApi} from "../../Services/employee";
import { getDocuments  as getDocumentsEmp ,deleteDocument as deleteDocumentApi, uploadDocument as uploadDocumentMgEmployeeApi} from "../../Services/documentapi";
import LOCAL_KEYS, { parseLocalStorageJSON } from "../../helpers/localstorage";
import { getValuesFromLocalstorage } from "../../components/Helpers";

export const EmployeeContext = createContext();

export const EmployeeProvider = ({ children }) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  const fetchEmployees = async (filters = {}) => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return { content: [], totalElements: 0, totalPages: 0 };
    }

    // Check if user has permission to access employees (only ADMIN and MKTG_EXEC)
    const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
    const userRole = userDetails?.acctType;
    if (userRole === "RECRUITER") {
      // Recruiters don't have access to employee endpoints
      setLoading(false);
      setEmployees([]);
      setTotalEmployees(0);
      setTotalPages(0);
      return { content: [], totalElements: 0, totalPages: 0 };
    }

    setLoading(true);
    try {
      const data = await getEmployees(filters);
      setEmployees(data.content || []);
      setTotalEmployees(data.totalElements || 0);
      setTotalPages(data.totalPages || 0);
      return data;
    } catch (error) {
      // Try to extract backend error message
      let backendMsg = error?.response?.data?.message || error?.message || "Failed to fetch employees";
      
      // If it's an authorization error, handle it gracefully
      if (backendMsg.includes("Authorization") || backendMsg.includes("Denied") || error?.response?.status === 403) {
        console.log("User does not have permission to access employees");
        setEmployees([]);
        setTotalEmployees(0);
        setTotalPages(0);
        setError(null); // Don't set error for authorization issues
        setLoading(false);
        return { content: [], totalElements: 0, totalPages: 0 };
      }
      
      setError(backendMsg);
      setEmployees([]);
      setTotalEmployees(0);
      setTotalPages(0);
      // Throw error so UI can show toast with correct message
      throw new Error(backendMsg);
    } finally {
      setLoading(false);
    }
  };
    
  const addEmployee = async (employeeData) => {
    try {
      await addEmployeeService(employeeData);
      await fetchEmployees(); // Refresh the list after adding
    } catch (error) {
      console.error("Failed to add employee:", error);
      throw error;
    }
  };

   const getEmployeeById = useCallback(async (id) => {
     
      try {
        const emp = await getEmployeeByIdApi(id);
        return emp;
      } catch (error) {
        throw error;
      } 
    }, []);

  const updateEmployee = async (id, employeeData) => {
    try {
      await updateEmployeeService(id, employeeData);
      await fetchEmployees(); // Refresh the list after updating
    } catch (error) {
      console.error("Failed to update employee:", error);
      throw error;
    }
  }
  const getbgCheck = async(id) => {
    try {
    const response = await getbgCheckApi(id);
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch background check data");
    }
  }
 const fetchEmployeeDocuments = async (employeeId, status) => {
  setLoading(true);
  setError(null);
  try {
    const apiStatus = status.toLowerCase();
    const data = await getDocumentsEmp(employeeId, apiStatus);
    setDocuments(data || []);
    return data; // ✅ FIX: return data so the calling function gets it
  } catch (err) {
    setError("Failed to load documents.");
    setDocuments([]);
    return []; // ✅ Return empty array to avoid undefined
  } finally {
    setLoading(false);
  }
};
const uploadDocumentMgEmployee = async (employeeId, documentType, issueDate, expiryDate, statusCode, file) => {
  try {
    // Use statusCode directly as it should be 'A' or 'D' from the form
    const finalStatusCode = statusCode;

    // Log all parameters before sending
    console.log("Uploading document with:", {
      employeeId, documentType, issueDate, expiryDate, finalStatusCode, file
    });

    const response = await uploadDocumentMgEmployeeApi(employeeId, documentType, issueDate, expiryDate, finalStatusCode, file);
    return response.data;
  } catch (error) {
    throw error; // Re-throw the original error directly
  }
};

  useEffect(() => {
    // Check if password is expired before making API call
    const isPasswordExpired = parseLocalStorageJSON(LOCAL_KEYS.PASSWORD_EXPIRED);
    if (isPasswordExpired) {
      setLoading(false);
      return;
    }

    // Check if user has permission to access employees
    const userDetails = getValuesFromLocalstorage(LOCAL_KEYS.USER_DETAILS);
    const userRole = userDetails?.acctType;
    
    // Only ADMIN and MKTG_EXEC can access employees
    if (userRole === "RECRUITER") {
      setLoading(false);
      setEmployees([]);
      setTotalEmployees(0);
      return;
    }

    const token = localStorage.getItem('access_token');
    if (token) {
      // Wrap in try-catch to handle authorization errors gracefully
      fetchEmployees({ stsCd: "A" }).catch((error) => {
        // Silently handle authorization errors - they're expected for some roles
        if (!error?.message?.includes("Authorization") && !error?.message?.includes("Denied")) {
          console.error("Failed to fetch employees:", error);
        }
      });
    }
  }, []);

  const deleteEmployeeDocument = async (employeeId, docType, currentStatus) => {
    setLoading(true);
    setError(null);
  
    try {
      const response = await deleteDocumentApi(employeeId, docType); 
  
      const updatedDocuments = await fetchEmployeeDocuments(employeeId, currentStatus);
  
      return {
        success: true,
        message: "Document deleted successfully",
        data: updatedDocuments,
      };
    } catch (error) {
      setError("Failed to delete document.");
      return {
        success: false,
        message: "Failed to delete document",
        error,
      };
    } finally {
      setLoading(false);
    }
  };
  
  
  
  return (
    <EmployeeContext.Provider 
      value={{ 
        employees, 
        loading, 
        fetchEmployees, 
        addEmployee,
        getEmployeeById,
        updateEmployee,
        getbgCheck,
        documents, 
        error, 
        fetchEmployeeDocuments, 
        deleteEmployeeDocument, 
        uploadDocumentMgEmployee,
        totalEmployees,
        totalPages
      }}
    >
      {children}
    </EmployeeContext.Provider>
  );
};